import { injectable, inject } from 'inversify';
import Debug from 'debug';
import {
  createQuery,
  TrackingService2,
  TrackingService2Symbol,
  createCommand
} from '@/app/tracking/api'
import { CompleteCourseCommandPayload, CompleteCourseCommand, UpdateCourseScoreCommand } from '@/app/course/tracking/api';

export type CourseCompletionSettings = {
  mode: 'final-exam'
} | {
  mode: 'section-percentage'
  passingSectionPercentage: number
} | {
  mode: 'none'
}

interface Score {
  min: number
  max: number
  raw: number
}

export const CourseCompletionServiceSymbol = Symbol.for('CourseCompletionService')

const debug = Debug('course-completion-service')

@injectable()
export class CourseCompletionService {

  private currentSettings?: CourseCompletionSettings

  constructor(@inject(TrackingService2Symbol)
  private trackingService: TrackingService2) {
  }

  public querySettings(): CourseCompletionSettings | null {
    return this.currentSettings || null
  }

  public updateSettings(settings: CourseCompletionSettings) {
    debug('updateSettings', settings)
    this.currentSettings = settings
  }

  public reportSectionProgress(completedSectionPercent: number) {

    if (!this.currentSettings) { return }

    if (this.currentSettings.mode !== 'section-percentage') {
      debug(`reportSectionProgrees; current mode is ${this.currentSettings.mode}, skipping`, completedSectionPercent)
      return
    }

    debug(`reportSectionProgrees`, completedSectionPercent)

    this.trackScore({
      min: 0,
      max: 100,
      raw: completedSectionPercent
    })

    if (completedSectionPercent >= this.currentSettings.passingSectionPercentage) {
      debug(`${completedSectionPercent}% of sections completed (threshold=${this.currentSettings.passingSectionPercentage}), we send course completed state`)

      this.trackCompletion()
    }
  }

  public reportPassedFinalExam(score: Score) {
    if (!this.currentSettings) { return }

    if (this.currentSettings.mode !== 'final-exam') {
      debug(`reportPassedFinalExam; current mode is ${this.currentSettings.mode}, skipping`, score)
      return
    }

    debug(`reportPassedFinalExam`, score)

    this.trackScore({
      min: score.min,
      max: score.max,
      raw: score.raw
    })

    this.trackCompletion()
  }

  private trackCompletion() {
    const payload: CompleteCourseCommandPayload = {
      successStatus: 'passed'
    }
    const command = createCommand<CompleteCourseCommand>('complete-course', payload)
    this.trackingService.execute(command)
  }

  private trackScore(score: Score) {
    const command = createCommand<UpdateCourseScoreCommand>('update-course-score', {
      score: {
        min: score.min,
        max: score.max,
        raw: score.raw
      },
      // we always overwrite, because there is a possibility that it depends on the aspect
      alwaysOverwrite: true
    })
    this.trackingService.execute(command)
  }
}
